import { ReactElement, useContext, useEffect, useMemo, useState } from "react";

import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";

import routes, { filterRoutesByPlan, IRouteProps } from "routes/routes";

import { alpha, Box, ThemeProvider } from '@mui/material';
import SidebarComponent from './components/SidebarComponent';
import DefaultLayout from './components/DefaultLayout';
import { createTheme } from '@mui/material/styles';
import { AppContext } from './context/AppContextProvider';
import { AppThemeName, backgroundColorDarkTheme } from './styles/global.styles';
import { AuthenticationProvider } from 'pages/AuthenticationPages/context/AuthenticationContextProvider';
import { api, getToken } from './services/DefaultService';
import { AxiosError } from 'axios';
import { IApiErrorData } from './services/types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt-br';
import { ptBR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import HeaderComponent from 'components/HeaderComponent';
import MapView from './components/MapComponent/MapView';
import { SyncJobProvider } from './context/SyncJobContext';
import "leaflet/dist/leaflet.css";

// Valores que mudam por tema (o resto do objeto de tema fica igual pros dois). BLUE é exatamente
// o que já existia hardcoded antes — zero mudança visual pra quem está no tema padrão.
const THEME_PALETTES: Record<
	AppThemeName,
	{
		bg: string;
		primary: string;
		primaryContrast: string;
		secondary: string;
		secondaryLight: string;
		infoContrast: string;
	}
> = {
	BLUE: {
		bg: '#0b1220',
		primary: '#1976d2',
		primaryContrast: '#ffffff',
		secondary: '#516f7f',
		secondaryLight: '#8c9ead',
		infoContrast: '#1a2035',
	},
	BLACK_WHITE: {
		bg: '#000000',
		primary: '#ffffff',
		primaryContrast: '#000000',
		secondary: '#555555',
		secondaryLight: '#b3b3b3',
		infoContrast: '#000000',
	},
};

const buildTheme = (themeName: AppThemeName) => {
	const p = THEME_PALETTES[themeName] ?? THEME_PALETTES.BLUE;

	return createTheme({
		palette: {
			background: {
				default: p.bg,
				paper: '#ffffff',
			},
			text: {
				primary: '#ffffff',
				secondary: p.secondaryLight,
				disabled: alpha('#ffffff', 0.6),
			},
			primary: {
				main: p.primary,
				contrastText: p.primaryContrast,
			},
			secondary: {
				main: p.secondary,
				light: p.secondaryLight,
				contrastText: '#ffffff',
			},
			error: {
				main: '#c40000',
				contrastText: '#ffffff',
			},
			warning: {
				main: '#c48e19',
				contrastText: '#ffffff',
			},
			info: {
				main: '#ffffff',
				contrastText: p.infoContrast,
			},
			success: {
				main: '#149b36',
				contrastText: '#ffffff',
			},
		},
		components: {
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundColor: backgroundColorDarkTheme,
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						'& .MuiInputBase-root': {
							'&:-webkit-autofill': {
								WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
								WebkitTextFillColor: '#000 !important',
								backgroundColor: 'transparent !important',
							},
						},
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						color: '#ffffff',
						'&.Mui-focused': {
							color: p.primary,
						},
						'&.Mui-disabled': {
							color: alpha('#ffffff', 0.6),
						},
					},
				},
			},
			MuiInput: {
				styleOverrides: {
					root: {
						color: '#ffffff',

						"&::before": {
							borderColor: p.secondary,
						},

						"&:hover:not(.Mui-disabled)::before": {
							borderColor: p.secondaryLight,
						},

						"&::after": {
							borderColor: p.primary,
						},

						"&.Mui-disabled": {
							color: alpha(p.secondary, 0.2),
							"&::before": {
								borderBottomStyle: "dotted",
								borderColor: alpha(p.secondary, 0.4),
							},
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: "6px",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: p.secondary,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: p.secondaryLight,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: p.primary,
						},
						"&.Mui-disabled": {
							backgroundColor: alpha(p.secondary, 0.2),
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: alpha(p.secondary, 0.4),
							},
						},
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						padding: 0,
					},
				},
			},
			// MuiListSubheader não é um Paper (não pega a sobrescrita de MuiPaper acima), então sem
			// isso ele usava o fundo branco padrão do MUI — aparecia em qualquer cabeçalho de grupo
			// (ex: Autocomplete com groupBy), destoando do resto do tema escuro.
			MuiListSubheader: {
				styleOverrides: {
					root: {
						backgroundColor: backgroundColorDarkTheme,
						color: p.secondaryLight,
					},
				},
			},
			// O cabeçalho de grupo do Autocomplete (groupBy) usa o slot `groupLabel` do próprio
			// MuiAutocomplete, não o MuiListSubheader (mesmo renderizando um ListSubheader por baixo,
			// a classe/slot de override é MuiAutocomplete-groupLabel) — por isso precisa da sua
			// própria sobrescrita aqui, senão continua com o fundo branco padrão do MUI.
			MuiAutocomplete: {
				styleOverrides: {
					groupLabel: {
						backgroundColor: backgroundColorDarkTheme,
						color: p.secondaryLight,
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						"&:hover": {
							backgroundColor: "#333",
						},
						"&.Mui-selected": {
							backgroundColor: "#444",
							"&:hover": {
								backgroundColor: "#555",
							},
						},
					},
				},
			},
		},
	});
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt-br");

export default function App() {
	const [isLogged, setIsLogged] = useState(false);
	const [isLoaded, setIsLoaded] = useState(false);
	const navigate = useNavigate();
	const { setIsShowLoadingComponent, showSnackbar, isRouteAllowed, theme: appThemeName } =
		useContext(AppContext);
	const { pathname } = useLocation();
	const theme = useMemo(() => buildTheme(appThemeName), [appThemeName]);
	// Memoizado por identidade estável: filterRoutesByPlan(routes) cria um array novo a cada
	// chamada, e o Sidebar reseta o estado de submenu aberto sempre que a prop `routes` muda de
	// referência — sem isso, qualquer re-render do App fechava o submenu que acabou de ser aberto.
	const menuRoutes = useMemo(() => filterRoutesByPlan(routes), [isLogged]);

	const getRoutes = (
		allRoutes: IRouteProps[],
	): ReactElement[] | ReactElement | [] =>
		allRoutes.flatMap((route: IRouteProps) => {
			if (route.collapse) {
				return getRoutes(route.collapse);
			}

			if (route.route) {
				const element = route.component;
				return <Route path={route.route} element={element} key={route.key} />;
			}

			return [];
		});

	const checkCurrentRoute = (routes: IRouteProps[], isLogged: boolean) => {
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].route === pathname) {
				if (routes[i].route === pathname && routes[i].protected && !isLogged) {
					navigate("/app/authentication/signIn");
				} else if (!routes[i].protected && isLogged) {
					let route = routes.find((r) => isRouteAllowed(r.permissionId));

					if (route?.collapse && route.collapse.length > 0) {
						route = route.collapse.find((r) => isRouteAllowed(r.permissionId));
					}
					if (route && route.route) navigate(route.route);
				}
				break;
			}
			if (routes[i].collapse && routes[i].collapse!.length > 0) {
				checkCurrentRoute(routes[i].collapse!, isLogged);
			}
		}
	};

	const routePath = (routes: IRouteProps[]) => {
		let path = "";
		routes.forEach((route) => {
			if (isRouteAllowed(route.permissionId)) {
				path = route.route ?? "";
				return;
			} else if (route.collapse && route.collapse?.length > 0) {
				path = routePath(route.collapse);
				return;
			}
		});
		return path;
	};

	const checkIsLoggedUser = () => {
		if (sessionStorage.getItem("token") == null) {
			setIsLogged(false);
			checkCurrentRoute(routes, false);
		} else {
			api.defaults.headers.common["Authorization"] =
				"Bearer " + window.atob(sessionStorage.getItem("token")!);
			checkCurrentRoute(routes, true);
			setIsLogged(true);
		}
		setIsLoaded(true);
	};

	const handleErrors = (error: AxiosError<any>) => {
		if (error.response && error.response.data) {
			const message =
				error.response.data.userMessage || "Erro interno do servidor!";
			switch (error.response.status) {
				case 0: {
					showSnackbar(
						"Falha ao conectar-se ao servidor. Verifique sua conexão Wi-Fi!",
						"warning",
					);
					break;
				}
				case 401: {
					showSnackbar(message, "warning");
					sessionStorage.removeItem("token");
					setTimeout(() => {
						window.location.href = "/app/authentication/sign-in";
					}, 500);
					break;
				}
				default: {
					showSnackbar(message, "error");
				}
			}
		}
	};

	useEffect(() => {
		api.interceptors.request.use(
			(config) => {
				const token = getToken();
				if (token)
					config.headers.Authorization = `Bearer ${window.atob(token)}`;
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

    api.interceptors.response.use(
      (config) => {
        return config;
      },
      (error) => {
        if (!error?.config?._suppressError) {
          handleErrors(error as AxiosError<IApiErrorData>);
        }
        return Promise.reject(error);
      },
    );
  }, []);

	useEffect(() => {
		checkIsLoggedUser();
	}, [pathname]);

	if (!isLoaded) {
		return null;
	}

  return (
    <ThemeProvider theme={theme}>
      <AuthenticationProvider>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={'pt-br'}
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <CssBaseline />
          {isLogged ? (
            <SyncJobProvider>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {window.innerWidth < 900 && (
                  <HeaderComponent routes={menuRoutes} />
                )}
                <Box sx={{ display: 'flex', height: '100%' }}>
                  {window.innerWidth > 900 && (
                    <SidebarComponent
                      brandName="AlertAi"
                      routes={menuRoutes}
                    />
                  )}
                </Box>
                <DefaultLayout>
                  <Routes>{getRoutes(routes)}</Routes>
                </DefaultLayout>
              </Box>
            </SyncJobProvider>
          ) : (
            <Routes>
              {getRoutes(routes)}
              <Route path="/map" element={<MapView selectedId={null} selectedIds={[]} onSelect={() => {}} />} />

							<Route
								path="*"
								element={<Navigate to="app/authentication/signIn" />}
							/>
						</Routes>
					)}
				</LocalizationProvider>
			</AuthenticationProvider>
		</ThemeProvider>
	);
}
